import { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';

// ── Data ──────────────────────────────────────────────────────────────────────

interface RateRow { item: string; unit: string; rate: string; notes?: string }
interface RateSection { title: string; icon: string; rows: RateRow[] }

const RATE_SECTIONS: RateSection[] = [
  {
    title: 'Framing — Installed (Material + Labour Blended)',
    icon: 'foundation',
    rows: [
      { item: 'Complete house framing — labour only', unit: '/SF floor area', rate: '$14.00', notes: 'Square foot rate: walls, floors, roof structure; BC 2026 range $10–18/sq ft' },
      { item: 'Complete house framing — labour + lumber package', unit: '/SF floor area', rate: '$32.00', notes: 'Square foot turn-key frame incl. material; BC 2026 range $28–40/sq ft' },
      { item: 'Garage / shop framing — labour only', unit: '/SF floor area', rate: '$11.00', notes: 'Square foot rate; simple rectangular plan, trussed roof' },
      { item: 'Exterior 2×6 wall complete', unit: '/SF wall area', rate: '$20.00', notes: 'Studs, plates, OSB sheathing all included — do not list OSB separately' },
      { item: 'Interior 2×4 partition', unit: '/SF wall area', rate: '$10.00', notes: 'Studs + plates only, no sheathing' },
      { item: 'Engineered floor system (TGI + subfloor)', unit: '/SF floor area', rate: '$16.00', notes: 'Sill, rim, TGI joists, 3/4" subfloor, hangers blended' },
      { item: '11-7/8" TGI I-joist installed', unit: '/LF', rate: '$6.50', notes: 'Supply + set; framing crew already on site' },
      { item: '9-1/2" TGI I-joist installed', unit: '/LF', rate: '$5.20' },
      { item: '3/4" T&G OSB subfloor', unit: '/SF', rate: '$2.10', notes: 'Gold Edge or equivalent, glue-and-nail' },
      { item: 'PT 2×6 sill plate', unit: '/LF', rate: '$8.50' },
      { item: 'Corbel rim board', unit: '/LF', rate: '$9.50' },
      { item: 'Roof trusses supply + crane set', unit: '/SF plan area', rate: '$12.50', notes: 'Pre-engineered; use plan area not sloped area' },
      { item: 'Roof sheathing 5/8" OSB', unit: '/SF', rate: '$1.65' },
      { item: 'Fascia + barge boards', unit: '/LF', rate: '$6.80' },
      { item: 'Tyvek housewrap', unit: '/SF', rate: '$0.85', notes: 'Separate from wall blended rate' },
      { item: 'H2.5A hurricane tie / clip', unit: '/EA', rate: '$3.40' },
      { item: 'Joist hanger LUS210', unit: '/EA', rate: '$5.40' },
    ],
  },
  {
    title: 'T&M Labour',
    icon: 'engineering',
    rows: [
      { item: 'Journeyman carpenter — T&M', unit: '/HR', rate: '$88.00', notes: 'GC rate to owner; covers wages, WCB, CPP/EI, overhead, profit' },
      { item: 'Beam install — 2-ply 2×10', unit: '/HR', rate: '$88.00', notes: 'Est. 3–5 HR; beam lumber billed separately' },
      { item: 'Beam install — 3-ply 2×12', unit: '/HR', rate: '$88.00', notes: 'Est. 5–8 HR' },
      { item: 'Beam install — 4-ply 2×12', unit: '/HR', rate: '$88.00', notes: 'Est. 6–10 HR' },
      { item: 'Large header >6\' RO', unit: '/HR', rate: '$88.00', notes: '6–10\': 4–8 HR | 10–16\': 6–12 HR' },
      { item: 'Vault / tall-wall / corbel framing', unit: '/HR', rate: '$88.00', notes: 'Allowance; define scope from plan notes' },
      { item: 'Complex truss bearing / site-cut', unit: '/HR', rate: '$88.00', notes: 'Hip/valley, bearing wall connections' },
    ],
  },
  {
    title: 'Roofing',
    icon: 'roofing',
    rows: [
      { item: 'Steel standing seam (PVDF coated)', unit: '/SF installed', rate: '$18.00', notes: 'BC 2026 range $14–22/SF; use $18 for quality residential spec' },
      { item: 'EPDM / TPO flat or low-slope', unit: '/SF installed', rate: '$10.50', notes: 'Includes membrane, insulation board, adhered' },
      { item: 'Asphalt shingle 30-yr architectural', unit: '/SF installed', rate: '$7.20' },
      { item: 'Ice + water shield', unit: '/SF', rate: '$1.80', notes: 'Full roof coverage in BC coastal climate' },
      { item: 'Drip edge', unit: '/LF', rate: '$2.80' },
    ],
  },
  {
    title: 'Exterior Envelope',
    icon: 'home_work',
    rows: [
      { item: 'HardiePlank fibre-cement siding', unit: '/SF installed', rate: '$10.75', notes: 'BC 2026; Vancouver contractor starting rate $9/SF; competitive mid-market $10.50–12' },
      { item: 'LP SmartSide', unit: '/SF installed', rate: '$8.20' },
      { item: 'Cedar bevel siding', unit: '/SF installed', rate: '$16.00' },
      { item: 'Vinyl siding', unit: '/SF installed', rate: '$6.20' },
      { item: 'Aluminum soffit vented', unit: '/SF', rate: '$8.50' },
      { item: 'Fascia painted', unit: '/LF', rate: '$6.80' },
      { item: 'Corner trim', unit: '/LF', rate: '$5.50' },
      { item: 'Metal window / door trim', unit: '/LF', rate: '$4.50' },
      { item: 'Eavestrough 5" aluminum', unit: '/LF', rate: '$14.00' },
    ],
  },
  {
    title: 'Windows',
    icon: 'window',
    rows: [
      { item: 'Window ≤24" wide', unit: '/EA installed', rate: '$560', notes: 'Triple-pane vinyl; first 2 digits of schedule code = width in inches' },
      { item: 'Window 24–36" wide', unit: '/EA installed', rate: '$720' },
      { item: 'Window 36–48" wide', unit: '/EA installed', rate: '$1,000' },
      { item: 'Window 48–60" wide', unit: '/EA installed', rate: '$1,400' },
      { item: 'Window >60" wide', unit: '/EA installed', rate: '$1,800', notes: 'Custom large units; verify against schedule' },
      { item: 'Fixed/picture window adjustment', unit: '— 15%', rate: '', notes: 'Applied to above tier price' },
      { item: 'Sill pan flashing', unit: '/EA', rate: '$45', notes: 'Every opening' },
    ],
  },
  {
    title: 'Exterior Doors',
    icon: 'door_front',
    rows: [
      { item: 'Fibreglass entry door', unit: '/EA installed', rate: '$2,600', notes: 'BC 2026; quality prehung fibreglass; $2,000–3,500 market range' },
      { item: 'Steel entry door', unit: '/EA installed', rate: '$1,500' },
      { item: 'Patio slider 6\'', unit: '/EA installed', rate: '$2,200' },
      { item: 'Patio slider 8\'', unit: '/EA installed', rate: '$3,000' },
      { item: 'Garage door 16×7 double (insulated steel)', unit: '/EA installed', rate: '$3,200', notes: 'Mid-grade insulated steel; $2,000–4,500 market range' },
      { item: 'Garage door 9×7 single', unit: '/EA installed', rate: '$1,600' },
      { item: 'Garage door opener (belt-drive)', unit: '/EA installed', rate: '$480', notes: 'Motor + installation; Canada market $350–500' },
    ],
  },
  {
    title: 'Insulation & Vapour Barrier',
    icon: 'heat',
    rows: [
      { item: 'R-22 batt — 2×6 exterior walls', unit: '/SF wall area', rate: '$1.65', notes: 'BC Building Code 2024 minimum for walls' },
      { item: 'R-50 blown-in — attic', unit: '/SF ceiling area', rate: '$2.85', notes: 'BC code minimum; blown cellulose or fibreglass' },
      { item: 'R-60 blown-in — attic (high-perf)', unit: '/SF ceiling area', rate: '$3.20' },
      { item: 'R-12 rim joist blanket', unit: '/LF perimeter', rate: '$1.25' },
      { item: '6-mil poly vapour barrier', unit: '/SF', rate: '$0.35', notes: 'Walls + ceiling coverage' },
    ],
  },
  {
    title: 'Concrete & Foundation',
    icon: 'layers',
    rows: [
      { item: 'Ready-mix 25 MPa supplied', unit: '/m³', rate: '$285', notes: 'Cubic meter supply, delivered — Cowichan Valley 2026; short-load fees extra' },
      { item: 'Ready-mix 32 MPa supplied', unit: '/m³', rate: '$315', notes: 'Cubic meter supply; exterior flatwork / driveways spec' },
      { item: 'Place + finish — footings', unit: '/m³', rate: '$190', notes: 'Cubic meter labour only; add supply rate above' },
      { item: 'Place + finish — formed walls', unit: '/m³', rate: '$260', notes: 'Cubic meter labour only; forming billed via /SF wall face rate' },
      { item: 'Place + finish — slabs', unit: '/m³', rate: '$210', notes: 'Cubic meter labour only; power-trowel finish' },
      { item: 'Supplied, placed & finished — blended', unit: '/m³', rate: '$510', notes: 'Cubic meter all-in when quoting by volume alone; range $460–560/m³' },
      { item: 'Concrete line pump', unit: '/HR', rate: '$210', notes: 'Typical 3–4 HR minimum + travel' },
      { item: 'Strip footing 16×8 form/pour/strip', unit: '/LF', rate: '$92' },
      { item: 'Strip footing 24×10', unit: '/LF', rate: '$115' },
      { item: 'Foundation wall face', unit: '/SF', rate: '$32' },
      { item: 'Slab 4" w/ wire mesh', unit: '/SF', rate: '$9.50' },
      { item: 'Anchor bolt 5/8"×12"', unit: '/EA', rate: '$9.50' },
      { item: 'Simpson HD5 hold-down', unit: '/EA', rate: '$58' },
      { item: 'Delta-MS drainage membrane', unit: '/SF', rate: '$2.80' },
      { item: 'Weeping tile 4"', unit: '/LF', rate: '$6.50' },
    ],
  },
];

// ── Labour cost breakdown ────────────────────────────────────────────────────

interface CostRow { component: string; basis: string; perHr: string }
const LABOUR_COST: CostRow[] = [
  { component: 'Base wage — journeyman (Vancouver Island median)', basis: 'Job Bank Canada 2025', perHr: '$32.00' },
  { component: 'CPP employer portion (5.95%)', basis: '2025 federal rate', perHr: '$1.90' },
  { component: 'EI employer portion (1.66% × 1.4 multiplier)', basis: '2025 federal rate', perHr: '$0.74' },
  { component: 'Vacation pay (BC 10% statutory)', basis: 'BC Employment Standards Act', perHr: '$3.20' },
  { component: 'WorkSafeBC — CU 721024 Framing (3.13%)', basis: 'WorkSafeBC 2025 Rate List', perHr: '$1.00' },
  { component: 'Benefits — health / dental (minimal)', basis: 'Market estimate', perHr: '$1.60' },
  { component: 'TOTAL direct employee cost', basis: '', perHr: '$40.44' },
  { component: 'Tools, truck, fuel, small equipment', basis: 'Overhead allocation', perHr: '$6.00' },
  { component: 'Office, insurance, admin', basis: 'Overhead allocation', perHr: '$4.00' },
  { component: 'Profit margin (~15%)', basis: 'GC margin', perHr: '$7.56' },
  { component: 'GC BILLABLE RATE TO OWNER', basis: 'OchrePoint T&M rate', perHr: '$88.00' },
];

// ── WCB classifications ──────────────────────────────────────────────────────

const WCB_RATES = [
  { cu: '721024', description: 'Framing or residential forming', rate: '3.13%', applies: 'Framing subcontractors' },
  { cu: '721027', description: 'House or other wood frame general contracting / construction', rate: '2.86%', applies: 'OchrePoint as GC' },
  { cu: '721021', description: 'Finishing carpentry', rate: '2.70%', applies: 'Trim, stairs, millwork subs' },
  { cu: '721028', description: 'Commercial / industrial / institutional construction', rate: '1.06%', applies: 'ICI projects only' },
];

// ── Labour-only sub market ───────────────────────────────────────────────────

const SUB_RATES = [
  { tier: 'Simple bungalow — standard 9\' walls, gable roof', low: '$4.50', mid: '$5.50', high: '$7.00' },
  { tier: 'Custom two-storey — moderate complexity', low: '$6.00', mid: '$7.50', high: '$9.50' },
  { tier: 'Complex custom — vaults, mixed heights, corbels', low: '$9.00', mid: '$10.50', high: '$13.00' },
];

// ── Component ────────────────────────────────────────────────────────────────

export function MarketRatesPage() {
  const [openSection, setOpenSection] = useState<string | null>('Framing — Installed (Material + Labour Blended)');
  const [activeTab, setActiveTab] = useState<'rates' | 'labour' | 'context'>('rates');
  const [search, setSearch] = useState('');

  // Filter sections by search term
  const filteredSections = search.trim()
    ? RATE_SECTIONS.map(s => ({
        ...s,
        rows: s.rows.filter(r =>
          r.item.toLowerCase().includes(search.toLowerCase()) ||
          (r.notes ?? '').toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(s => s.rows.length > 0 || s.title.toLowerCase().includes(search.toLowerCase()))
    : RATE_SECTIONS;

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="p-3 bg-ochre-100 rounded-xl">
            <span className="material-icons text-ochre-600 text-3xl">price_check</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">BC Market Rates Reference</h1>
            <p className="text-gray-500 mt-0.5">
              Vancouver Island / Cowichan Valley · Researched June 2026 · GC-to-owner installed pricing ($CAD)
            </p>
          </div>
        </div>

        {/* Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex gap-3">
          <span className="material-icons text-amber-600 text-lg mt-0.5 flex-shrink-0">info</span>
          <p className="text-sm text-amber-800">
            These are <strong>GC-direct-to-owner</strong> rates — material + labour + overhead + profit blended into
            one unit price. A labour-only framing subcontractor will quote 30–40% less; that's expected and correct,
            since their quote excludes materials, project management, supervision, and warranty.
          </p>
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {(['rates', 'labour', 'context'] as const).map(t => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === t
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'rates' ? 'Installed Rates' : t === 'labour' ? 'Labour Cost Build-Up' : 'Market Context'}
              </button>
            ))}
          </div>
          {activeTab === 'rates' && (
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-base">search</span>
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); if (e.target.value) setOpenSection(null); }}
                placeholder="Search rates…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <span className="material-symbols-outlined text-sm leading-none">close</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Tab: Installed Rates ── */}
        {activeTab === 'rates' && (
          <div className="space-y-3">
            {search && filteredSections.length === 0 && (
              <div className="py-12 text-center text-gray-500 text-sm">
                <span className="material-symbols-outlined text-4xl text-gray-300 block mb-3">search_off</span>
                No rates match "{search}"
              </div>
            )}
            {filteredSections.map(section => (
              <div key={section.title} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                  onClick={() => !search && setOpenSection(openSection === section.title ? null : section.title)}
                >
                  <div className="flex items-center gap-3">
                    <span className="material-icons text-ochre-500">{section.icon}</span>
                    <span className="font-semibold text-gray-800">{section.title}</span>
                    <span className="text-xs text-gray-400">{section.rows.length} items</span>
                  </div>
                  <span className="material-icons text-gray-400">
                    {openSection === section.title ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {(openSection === section.title || !!search) && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-4 py-2 font-medium text-gray-500 w-[40%]">Item</th>
                          <th className="px-4 py-2 font-medium text-gray-500 w-[15%]">Unit</th>
                          <th className="px-4 py-2 font-medium text-gray-500 w-[12%] text-right">Rate</th>
                          <th className="px-4 py-2 font-medium text-gray-500">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(search ? section.rows : section.rows).map((row, i) => (
                          <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                            <td className="px-4 py-2.5 text-gray-800 font-medium">{row.item}</td>
                            <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{row.unit}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-ochre-700 font-mono">{row.rate}</td>
                            <td className="px-4 py-2.5 text-gray-500 text-xs">{row.notes ?? ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Tab: Labour Cost Build-Up ── */}
        {activeTab === 'labour' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="material-icons text-ochre-500">calculate</span>
                  T&M Rate Build-Up — $88/HR Justification
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Based on non-union Vancouver Island journeyman carpenter, GC billing to owner</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2 text-left font-medium text-gray-500 w-[55%]">Component</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Source</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">$/hr</th>
                  </tr>
                </thead>
                <tbody>
                  {LABOUR_COST.map((row, i) => {
                    const isSummary = row.component.startsWith('TOTAL') || row.component.startsWith('GC BILLABLE');
                    return (
                      <tr key={i} className={`border-t border-gray-100 ${isSummary ? 'bg-ochre-50 font-semibold' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                        <td className={`px-4 py-2.5 ${isSummary ? 'text-ochre-800' : 'text-gray-700'}`}>{row.component}</td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs">{row.basis}</td>
                        <td className={`px-4 py-2.5 text-right font-mono ${isSummary ? 'text-ochre-700 text-base' : 'text-gray-700'}`}>{row.perHr}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="material-icons text-ochre-500">verified</span>
                  WorkSafeBC 2025 Premium Rates
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">CU = Classification Unit · Rate = % of gross payroll · Source: WorkSafeBC 2025 Classification and Rate List</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2 text-left font-medium text-gray-500 w-[10%]">CU</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 w-[45%]">Description</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500 w-[12%]">2025 Rate</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Applies to</th>
                  </tr>
                </thead>
                <tbody>
                  {WCB_RATES.map((r, i) => (
                    <tr key={i} className={`border-t border-gray-100 ${r.cu === '721027' ? 'bg-ochre-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{r.cu}</td>
                      <td className={`px-4 py-2.5 ${r.cu === '721027' ? 'text-ochre-800 font-medium' : 'text-gray-700'}`}>{r.description}</td>
                      <td className={`px-4 py-2.5 text-right font-semibold font-mono ${r.cu === '721027' ? 'text-ochre-700' : 'text-gray-700'}`}>{r.rate}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{r.applies}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="material-icons text-ochre-500">people</span>
                  Carpenter Wage Benchmarks — BC 2025
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Source: Job Bank Canada, BCRCC Industrial Agreement, ChiselJobs</p>
              </div>
              <div className="divide-y divide-gray-100">
                {[
                  { label: 'Non-union residential journeyman — Vancouver Island (Job Bank median)', wage: '$32.00/hr', type: 'Non-union' },
                  { label: 'Non-union residential journeyman — BC range (25th–75th pct)', wage: '$23–$44/hr', type: 'Non-union' },
                  { label: 'Red Seal journeyman nationally (ChiselJobs)', wage: '$34–$44/hr', type: 'Non-union' },
                  { label: 'BCRCC Industrial — Journeyman, Outside Lower Mainland (Vancouver Island)', wage: '$47.22/hr', type: 'Union ICI' },
                  { label: 'BCRCC Industrial — Foreman (115%), Outside Lower Mainland', wage: '$54.30/hr', type: 'Union ICI' },
                  { label: 'Union Pacific Northwest all-in (wages + benefits + pension)', wage: '$65–$95/hr', type: 'Union ICI' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm text-gray-700">{item.label}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${item.type === 'Union ICI' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {item.type}
                      </span>
                    </div>
                    <span className="font-semibold font-mono text-gray-800 ml-4 whitespace-nowrap">{item.wage}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Market Context ── */}
        {activeTab === 'context' && (
          <div className="space-y-6">

            {/* GC vs Sub pricing explanation */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="material-icons text-ochre-500">account_balance</span>
                  GC Pricing vs. Framing Sub Pricing
                </h2>
              </div>
              <div className="p-4 space-y-4 text-sm text-gray-700">
                <p>
                  When OchrePoint prices a job, the estimate reflects <strong>GC-direct-to-owner rates</strong> —
                  material + labour + overhead + profit blended together. This is the appropriate number for a
                  client-facing quote.
                </p>
                <p>
                  A labour-only framing subcontractor quotes only their crew's time. The GC (us) separately buys all
                  materials and adds project management, supervision, warranty coverage, and margin. The difference is
                  not markup for its own sake — it's what pays for the business to function.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                  {[
                    {
                      label: 'Framing Sub (labour only)', color: 'border-blue-200 bg-blue-50',
                      items: ['Crew wages only', 'No materials', 'No project management', 'No warranty liability', 'No design coordination', '~$7–$9/SF floor area for complex custom'],
                    },
                    {
                      label: 'OchrePoint GC Price to Owner', color: 'border-ochre-300 bg-ochre-50',
                      items: ['Labour + materials blended', 'Full material procurement', 'Site supervision daily', '2-year defect liability', 'Plan coordination + RFIs', '~$16–$24/SF floor area (structural phase)'],
                    },
                  ].map(card => (
                    <div key={card.label} className={`rounded-xl border p-4 ${card.color}`}>
                      <p className="font-semibold text-gray-800 mb-2">{card.label}</p>
                      <ul className="space-y-1">
                        {card.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                            <span className="material-icons text-gray-400 text-sm mt-0.5">check_small</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Labour-only sub bid ranges */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="material-icons text-ochre-500">format_list_numbered</span>
                  Labour-Only Sub Bid Norms — BC Residential 2025 (per SF floor area)
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Sources: EB3 Construction, bhumicalculator.com BC data, national framing cost surveys
                </p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2 text-left font-medium text-gray-500 w-[50%]">Project Type</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Low</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Mid</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">High</th>
                  </tr>
                </thead>
                <tbody>
                  {SUB_RATES.map((r, i) => (
                    <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                      <td className="px-4 py-2.5 text-gray-700">{r.tier}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-green-700">{r.low}</td>
                      <td className="px-4 py-2.5 text-center font-mono font-semibold text-gray-800">{r.mid}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-red-600">{r.high}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
                GNB Reiber Road (2,800 SF, complex custom with vaults + corbels): estimated labour-only sub bid ~$28,000–$38,200 for
                structural draws. Our estimate at GC rates ($142K for Draw 1+2) is appropriately 3–4× higher — the difference is
                materials, equipment, supervision, liability, and margin.
              </div>
            </div>

            {/* Tariff / lumber market note */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="material-icons text-ochre-500">trending_up</span>
                  2025–2026 Market Conditions
                </h2>
              </div>
              <div className="p-4 space-y-3 text-sm text-gray-700">
                {[
                  {
                    icon: 'forest', title: 'US Softwood Lumber Tariffs at 35%+',
                    body: 'US duties on Canadian softwood lumber (CVD + Section 232) now total 34.83–35.9%. Net effect on BC domestic lumber: relatively stable, as BC mills can\'t export to US as easily. Domestic framing lumber composite ~$567–$872/MBF in early 2026. The $20/SF blended wall rate absorbs ±10% lumber swings.',
                  },
                  {
                    icon: 'construction', title: 'Labour Availability Improving (2026)',
                    body: 'Altus Group\'s 2026 Canadian Cost Guide notes "labour is more readily available for private work" in BC in 2026, as fewer new projects are starting. This reduces the scarcity premium that was pushing rates up in 2022–2024.',
                  },
                  {
                    icon: 'price_change', title: 'Overall BC Construction Costs',
                    body: 'Altus 2026: Vancouver experienced overall construction cost declines in 2025, driven by a cooled condo tower segment. Mid-range custom single-family ($400–$550/SF total) continues to hold. The framing + lock-up scope (our three draws) typically represents 30–40% of total build cost.',
                  },
                  {
                    icon: 'verified_user', title: 'BC Building Code 2024 Impact',
                    body: 'BCBC 2024 tightened thermal envelope requirements: R-22 (2×6 walls), R-50 minimum attic. Step Code compliance is increasingly required in new Cowichan Valley permits. Our insulation rates already reflect these minimums.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="material-icons text-ochre-500 flex-shrink-0">{item.icon}</span>
                    <div>
                      <p className="font-semibold text-gray-800">{item.title}</p>
                      <p className="text-gray-600 mt-0.5">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sources */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Research Sources — June 2026</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-gray-500">
                {[
                  'Job Bank Canada — Carpenter Wages, BC (2024–2025)',
                  'WorkSafeBC 2025 Classification and Rate List (CU 721024, 721027)',
                  'BCRCC Craft Carpenters Standard Industrial Agreement 2023–2026 (Schedule A2)',
                  'Paragon Roofing BC — Metal Roof Cost Vancouver 2025',
                  'Siding in Vancouver — HardiePlank installed $9+/SF',
                  'Reno Quotes — Garage Door Prices Canada 2025',
                  'Magic Window Canada — Window Prices by Size 2025',
                  'Altus Group 2026 Canadian Cost Guide',
                  'bhumicalculator.com — Framer Cost Canada 2026 (BC $16–38/SF installed)',
                  'EB3 Construction — Framing Labor Per Square Foot (West Coast $5–8/SF wall)',
                  'Böehm Construction (Nanaimo) — Vancouver Island custom home $275–350/SF',
                  'CBC News — US softwood lumber duties 34.83–35.9% (2025–2026)',
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="material-icons text-gray-400 text-xs mt-0.5">circle</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        )}

      </div>
    </MainLayout>
  );
}
